import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CaretDownIcon } from '@phosphor-icons/react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import {
  formatCurrency,
  assignCategoryColors,
  getCategoryColor,
  splitCategoryEmoji,
  stripCategoryEmoji,
} from '@budget/core';
import type { DebtCategory, DebtItem, DebtsData, Tri } from '@budget/core';
import { useDashboard } from '../data/useDashboard';
import { type Scope } from '../components/ScopeToggle';
import { useScope } from '../hooks/useScope';
import { PersonBreakdown } from '../components/PersonBreakdown';
import { BreakdownLegend } from '../components/BreakdownLegend';
import { BreakdownLegendGrid } from '../components/BreakdownLegendGrid';
import { DonutCenterEmoji } from '../components/DonutCenterEmoji';
import { CardHeader } from '../components/CardHeader';
import { ProgressFill } from '../components/ProgressFill';
import {
  StatGrid,
  StatItem,
  StatSubText,
} from '../components/StatBlock';
import { getCategoryIcon } from '../components/CategoryIcon';
import { ThirdPartyLogo } from '../components/ThirdPartyLogos';
import { useChartReady } from '../hooks/useChartReady';
import { getPersonName } from '../config/personNames';
import clsx from 'clsx';

const TOOLTIP_STYLE = {
  background: 'var(--berg-bg-elevated)',
  border: '1px solid var(--berg-border-strong)',
  borderRadius: 12,
  fontSize: 12,
  color: 'var(--berg-text)',
  boxShadow: '0 4px 24px rgba(0,0,0,0.25)',
};
const TOOLTIP_LABEL_STYLE = { color: 'var(--berg-text)', fontWeight: 600 };
const TOOLTIP_ITEM_STYLE = { color: 'var(--berg-text)' };

function fmt0(n: number) {
  return formatCurrency(n, 'USD', 'en-US', 0);
}

function fmtCents(n: number) {
  return formatCurrency(n);
}

function pickScope(t: Tri, scope: Scope): number {
  return t[scope];
}

function pct(n: number) {
  return `${(n * 100).toFixed(2)}%`;
}

// DOM ids for deep-linking from the Net Worth tab. Stripping the emoji + any
// non-alphanumeric characters keeps the slug stable even if a category's
// emoji changes in the sheet.
function categoryDomId(rawName: string): string {
  const bare = splitCategoryEmoji(rawName).name;
  const slug = bare
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
  return `debt-cat-${slug}`;
}

// Cost columns (minimum payment, monthly interest, etc.) live as a single
// household scalar in the sheet. When viewing a per-person scope, apportion
// by that person's share of the household balance — falls back to 0 when the
// household balance is 0.
function apportion(amount: number, balance: Tri, scope: Scope): number {
  if (scope === 'household') return amount;
  if (balance.household === 0) return 0;
  return amount * (balance[scope] / balance.household);
}

// Productive = asset-backed or future-earning (mortgage, student loans, auto).
// Consumer = high-rate, no asset (credit cards, short-term loans).
// Other = everything else (family loans, unusual categories) — kept separate
// because zero-interest interpersonal debt doesn't fit either framing.
type DebtClass = 'productive' | 'consumer' | 'other';

function classifyDebt(categoryName: string): DebtClass {
  const bare = stripCategoryEmoji(categoryName);
  if (/mortgage|equity|student/i.test(bare)) return 'productive';
  if (/credit\s?card|short[\s-]?term|auto\s?loan|car\s?loan/i.test(bare)) return 'consumer';
  return 'other';
}

// Colors come from the shared CATEGORY_COLORS map (via getCategoryColor)
// so every segmented bar in the app reaches for one source of truth.
const DEBT_CLASS_META: Record<DebtClass, { label: string; color: string }> = {
  productive: { label: 'Productive', color: getCategoryColor('Productive') },
  consumer: { label: 'Consumer', color: getCategoryColor('Consumer') },
  other: { label: 'Other', color: getCategoryColor('Other') },
};

function HeroDebts({ debts, scope }: { debts: DebtsData; scope: Scope }) {
  // Sum categories rather than trusting the sheet's "Total Debts" row, so the
  // hero number always matches the sum of the cards below.
  const totalBalance: Tri = debts.categories.reduce<Tri>(
    (acc, c) => ({
      household: acc.household + c.balance.household,
      dakota: acc.dakota + c.balance.dakota,
      natalia: acc.natalia + c.balance.natalia,
    }),
    { household: 0, dakota: 0, natalia: 0 }
  );
  const balance = pickScope(totalBalance, scope);

  const monthlyInterestHousehold = debts.categories.reduce(
    (sum, c) => sum + c.monthlyInterest,
    0
  );
  const yearlyInterestHousehold = debts.categories.reduce(
    (sum, c) => sum + c.yearlyInterest,
    0
  );
  const minPaymentHousehold = debts.categories.reduce(
    (sum, c) => sum + c.minimumPayment,
    0
  );
  const minPrincipalHousehold = debts.categories.reduce(
    (sum, c) => sum + c.minimumPrincipal,
    0
  );

  const monthlyInterest = apportion(monthlyInterestHousehold, totalBalance, scope);
  const yearlyInterest = apportion(yearlyInterestHousehold, totalBalance, scope);
  const minPayment = apportion(minPaymentHousehold, totalBalance, scope);
  const minPrincipal = apportion(minPrincipalHousehold, totalBalance, scope);

  const buckets: Record<DebtClass, { balance: number; monthlyInterest: number }> = {
    productive: { balance: 0, monthlyInterest: 0 },
    consumer: { balance: 0, monthlyInterest: 0 },
    other: { balance: 0, monthlyInterest: 0 },
  };
  for (const c of debts.categories) {
    const cls = classifyDebt(c.name);
    buckets[cls].balance += pickScope(c.balance, scope);
    buckets[cls].monthlyInterest += apportion(c.monthlyInterest, c.balance, scope);
  }
  const totalForShare = pickScope(totalBalance, scope);
  const productiveShare =
    totalForShare > 0 ? (buckets.productive.balance / totalForShare) * 100 : 0;
  // Cross-element hover sync between the debt-mix bar and its legend cells,
  // mirroring the liquidity bar on the Assets hero.
  const [hoveredBucket, setHoveredBucket] = useState<DebtClass | null>(null);
  // Negative principal means the min payment doesn't cover the interest —
  // the balance is growing under minimums. Surface in red when that happens
  // (matches the per-row styling in DebtItemRow).
  const principalPct = minPayment > 0 ? (minPrincipal / minPayment) * 100 : 0;
  const negativePrincipal = minPrincipal < 0;

  return (
    <div className="card flex flex-col gap-5 h-full">
      <div className="flex items-end justify-between gap-6 flex-wrap">
        <div className="flex flex-col gap-1">
          <span className="hero-eyebrow">
            {scope === 'household'
              ? 'Total debt'
              : `${getPersonName(scope)} debt`}
          </span>
          <div className="hero-balance tabular leading-none">{fmt0(balance)}</div>
        </div>
        {scope === 'household' && (
          <PersonBreakdown
            dakota={totalBalance.dakota}
            natalia={totalBalance.natalia}
            digits={0}
          />
        )}
      </div>

      <div className="flex flex-col gap-3 border-t border-border pt-4">
        <div className="flex items-center justify-between">
          <span className="stat-label">Debt mix</span>
          <span className="text-xs text-fg-tertiary tabular">
            {productiveShare.toFixed(0)}% productive
          </span>
        </div>
        <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-subtle gap-0.5">
          {(['productive', 'consumer', 'other'] as const).map((cls) => {
            const pct =
              totalForShare > 0 ? (buckets[cls].balance / totalForShare) * 100 : 0;
            if (pct <= 0) return null;
            const dimmed = hoveredBucket !== null && hoveredBucket !== cls;
            return (
              <ProgressFill
                key={cls}
                pct={pct}
                color={DEBT_CLASS_META[cls].color}
                style={{ opacity: dimmed ? 0.2 : 1 }}
                onMouseEnter={() => setHoveredBucket(cls)}
                onMouseLeave={() => setHoveredBucket(null)}
              />
            );
          })}
        </div>
        <div className="hidden sm:block">
          <BreakdownLegendGrid
            items={(['productive', 'consumer', 'other'] as const).map((cls) => ({
              key: cls,
              label: DEBT_CLASS_META[cls].label,
              value: buckets[cls].balance,
              color: DEBT_CLASS_META[cls].color,
            }))}
            total={totalForShare}
            formatValue={fmt0}
            hovered={hoveredBucket}
            onHoverChange={(k) =>
              setHoveredBucket(k as 'productive' | 'consumer' | 'other' | null)
            }
          />
        </div>
        <div className="sm:hidden">
          <BreakdownLegend
            items={(['productive', 'consumer', 'other'] as const).map((cls) => ({
              key: cls,
              label: DEBT_CLASS_META[cls].label,
              value: buckets[cls].balance,
              color: DEBT_CLASS_META[cls].color,
            }))}
            total={totalForShare}
            formatValue={fmt0}
            hovered={hoveredBucket}
            onHoverChange={(k) =>
              setHoveredBucket(k as 'productive' | 'consumer' | 'other' | null)
            }
            collapseAfter={Infinity}
          />
        </div>
      </div>

      <StatGrid columns={2} gap={6} className="border-t border-border pt-4">
        <StatItem
          label="Monthly interest"
          valueSize="xl"
          valueTone="neg"
          value={`-${fmtCents(monthlyInterest)}`}
          sub={<StatSubText unit="cost of carry" />}
        />
        <StatItem
          label="Annual interest"
          valueSize="xl"
          valueTone="neg"
          value={`-${fmt0(yearlyInterest)}`}
          sub={<StatSubText unit="at current rates" />}
        />
      </StatGrid>

      <StatGrid columns={2} gap={6} className="border-t border-border pt-4">
        <StatItem
          label="Min monthly payment"
          valueSize="xl"
          value={fmtCents(minPayment)}
          sub={<StatSubText unit="required to stay current" />}
        />
        <StatItem
          label="Min principal payment"
          valueSize="xl"
          valueTone={negativePrincipal ? 'neg' : undefined}
          value={fmtCents(minPrincipal)}
          sub={
            minPayment > 0 ? (
              <StatSubText
                amount={`${principalPct.toFixed(0)}%`}
                unit="of min payment"
              />
            ) : (
              <StatSubText unit="no min payment" />
            )
          }
        />
      </StatGrid>
    </div>
  );
}

function DebtsBreakdownCard({ debts, scope }: { debts: DebtsData; scope: Scope }) {
  const colorMap = useMemo(
    () => assignCategoryColors(debts.categories.map((c) => c.name)),
    [debts.categories]
  );

  const breakdown = useMemo(() => {
    return debts.categories
      .map((c) => ({
        label: c.name,
        value: pickScope(c.balance, scope),
        household: c.balance.household,
        color: colorMap.get(c.name) ?? 'var(--berg-neutral)',
      }))
      // Sort by the active scope's value so the legend reads largest-first
      // for whichever person is being viewed.
      .sort((a, b) => b.value - a.value);
  }, [debts.categories, scope, colorMap]);

  const pieData = useMemo(() => breakdown.filter((b) => b.value > 0), [breakdown]);
  const total = breakdown.reduce((sum, b) => sum + b.value, 0);
  const [hovered, setHoveredRaw] = useState<string | null>(null);
  // Gate hover-dim until the entry animation completes (recharts cancels
  // the swoop-in when child props like fillOpacity change mid-mount).
  const ready = useChartReady();
  const setHovered = (next: string | null) => {
    if (ready) setHoveredRaw(next);
  };

  return (
    <div className="card">
      <CardHeader
        title="Breakdown"
        subtitle="By category"
        right={<span className="tabular text-2xl font-semibold">{fmt0(total)}</span>}
      />
      {pieData.length === 0 ? (
        <div className="empty-state py-8">No debt in this scope.</div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="relative h-[220px]">
            <DonutCenterEmoji
              emoji={hovered ? splitCategoryEmoji(hovered).emoji : null}
            />
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="value"
                  animationDuration={1000}
                  nameKey="label"
                  innerRadius="60%"
                  outerRadius="98%"
                  startAngle={90}
                  endAngle={-270}
                  paddingAngle={0.5}
                  stroke="var(--berg-bg-elevated)"
                  strokeWidth={1}
                  onMouseEnter={(_, idx) => setHovered(pieData[idx]?.label ?? null)}
                  onMouseLeave={() => setHovered(null)}
                >
                  {pieData.map((b) => (
                    <Cell
                      key={b.label}
                      fill={b.color}
                      fillOpacity={hovered === null || hovered === b.label ? 1 : 0.2}
                      style={{ transition: 'fill-opacity 0.18s ease' }}
                    />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(v) => fmtCents(typeof v === 'number' ? v : 0)}
                  contentStyle={TOOLTIP_STYLE}
                  labelStyle={TOOLTIP_LABEL_STYLE}
                  itemStyle={TOOLTIP_ITEM_STYLE}
                  cursor={{ fill: 'transparent' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <BreakdownLegend
            items={breakdown.map((b) => ({
              key: b.label,
              label: stripCategoryEmoji(b.label),
              value: b.value,
              color: b.color,
            }))}
            total={total}
            formatValue={fmt0}
            hovered={hovered}
            onHoverChange={setHovered}
          />
        </div>
      )}
    </div>
  );
}

function DebtCategoryCard({
  category,
  scope,
  totalBalance,
  expanded,
  onToggle,
  domId,
}: {
  category: DebtCategory;
  scope: Scope;
  totalBalance: number;
  expanded: boolean;
  onToggle: () => void;
  domId?: string;
}) {
  const { emoji, name: bareName } = splitCategoryEmoji(category.name);
  const Icon = emoji ? null : getCategoryIcon(category.name);
  const balance = pickScope(category.balance, scope);
  const monthlyInterest = apportion(category.monthlyInterest, category.balance, scope);
  const share = totalBalance > 0 ? (balance / totalBalance) * 100 : 0;

  // Hide items with zero balance in the current scope — typically already-
  // paid-off accounts that are kept in the sheet for history.
  const visibleItems = category.items.filter((i) => pickScope(i.balance, scope) !== 0);
  const paidOffCount = category.items.length - visibleItems.length;

  return (
    <div id={domId} className="card-flush scroll-mt-20">
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-4 px-5 py-4 max-md:px-4 text-left hover:bg-overlay transition-colors"
      >
        {emoji ? (
          <span aria-hidden className="w-8 flex-none text-center text-2xl leading-none">
            {emoji}
          </span>
        ) : (
          Icon && (
            <Icon
              size={22}
              weight="duotone"
              className="text-fg-secondary flex-none"
            />
          )
        )}
        <div className="flex-1 min-w-0">
          <div className="font-semibold truncate">{bareName}</div>
          <div className="text-xs text-fg-tertiary mt-0.5">
            {visibleItems.length} {visibleItems.length === 1 ? 'debt' : 'debts'} ·{' '}
            {share.toFixed(1)}%
            {paidOffCount > 0 && <> · {paidOffCount} paid off</>}
          </div>
        </div>
        <div className="flex items-center gap-3 flex-none">
          <div className="flex flex-col items-end gap-0.5">
            <span className="tabular font-semibold text-base">{fmt0(balance)}</span>
            {monthlyInterest > 0 && (
              <span className="text-xs delta-neg tabular">
                -{fmtCents(monthlyInterest)}/mo
              </span>
            )}
          </div>
          <CaretDownIcon
            size={16}
            weight="bold"
            className={clsx(
              'text-fg-tertiary transition-transform',
              expanded && 'rotate-180'
            )}
          />
        </div>
      </button>

      {expanded && visibleItems.length > 0 && (
        <div className="border-t border-border">
          <div className="grid grid-cols-[1fr_4rem_6rem_7rem_6rem] gap-x-4 px-5 py-2 border-b border-border text-[0.6875rem] uppercase tracking-[0.08em] font-medium text-fg-tertiary max-md:hidden">
            <div className="pl-[48px]">Debt</div>
            <div className="text-right">APR</div>
            <div className="text-right">Min/mo</div>
            <div className="text-right">Final payment</div>
            <div className="text-right">Balance</div>
          </div>
          {visibleItems.map((item) => (
            <DebtItemRow key={item.name} item={item} scope={scope} />
          ))}
        </div>
      )}
    </div>
  );
}

function DebtItemRow({ item, scope }: { item: DebtItem; scope: Scope }) {
  const balance = pickScope(item.balance, scope);
  const minPayment = apportion(item.minimumPayment, item.balance, scope);
  // A negative principal contribution means the minimum payment doesn't even
  // cover monthly interest — the balance is growing. Surface it in red.
  const principal = apportion(item.minimumPrincipal, item.balance, scope);
  const negativePrincipal = principal < 0;
  const hasAPR = item.interestRate > 0;
  const hasMinPayment = minPayment > 0;
  const hasFinal = Boolean(item.finalPayment);

  return (
    <>
      <div className="grid grid-cols-[1fr_4rem_6rem_7rem_6rem] gap-x-4 items-center px-5 py-2.5 border-b border-border last:border-b-0 hover:bg-overlay transition-colors max-md:hidden">
        <div className="flex items-center gap-4 text-sm text-fg-secondary truncate">
          <span className="w-8 flex-none flex items-center justify-center">
            <ThirdPartyLogo location={item.location} size={20} />
          </span>
          <span className="truncate">{item.name}</span>
        </div>
        <div className="text-right tabular text-sm text-fg-tertiary">
          {hasAPR ? pct(item.interestRate) : '—'}
        </div>
        <div className="text-right tabular text-sm">
          {hasMinPayment ? (
            <span className={clsx(negativePrincipal && 'delta-neg')}>
              {fmtCents(minPayment)}
            </span>
          ) : (
            <span className="text-fg-tertiary">—</span>
          )}
        </div>
        <div className="text-right tabular text-sm text-fg-tertiary truncate">
          {item.finalPayment || '—'}
        </div>
        <div className="text-right tabular text-sm font-medium">{fmt0(balance)}</div>
      </div>
      <div className="hidden max-md:flex flex-col gap-1 px-4 py-2.5 border-b border-border last:border-b-0">
        <div className="flex items-center gap-4 text-sm">
          <span className="w-8 flex-none flex items-center justify-center">
            <ThirdPartyLogo location={item.location} size={20} />
          </span>
          <span className="text-fg-secondary truncate flex-1 min-w-0">
            {item.name}
          </span>
          <span className="tabular font-medium shrink-0">{fmt0(balance)}</span>
        </div>
        {(hasAPR || hasMinPayment || hasFinal) && (
          <div className="flex items-baseline justify-between gap-3 pl-[48px] text-xs tabular">
            <span className="text-fg-tertiary truncate flex items-baseline gap-1.5">
              {hasAPR && <span>{pct(item.interestRate)} APR</span>}
              {hasAPR && hasMinPayment && <span>·</span>}
              {hasMinPayment && (
                <span className={clsx(negativePrincipal && 'delta-neg')}>
                  {fmtCents(minPayment)}/mo
                </span>
              )}
            </span>
            {hasFinal && (
              <span className="text-fg-tertiary shrink-0">{item.finalPayment}</span>
            )}
          </div>
        )}
      </div>
    </>
  );
}

export function DebtsPage({ embedded = false }: { embedded?: boolean } = {}) {
  const { data, isLoading, error } = useDashboard();
  const { scope } = useScope();
  const [expandedSet, setExpandedSet] = useState<Set<string>>(new Set());
  const [searchParams, setSearchParams] = useSearchParams();
  const catParam = searchParams.get('cat');

  const debts = data?.debts;

  const visibleCategories = useMemo(() => {
    if (!debts) return [];
    return [...debts.categories].sort(
      (a, b) => pickScope(b.balance, scope) - pickScope(a.balance, scope)
    );
  }, [debts, scope]);

  const toggle = (name: string) =>
    setExpandedSet((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });

  const totalBalance = debts
    ? debts.categories.reduce((sum, c) => sum + pickScope(c.balance, scope), 0)
    : 0;

  // Deep-link from the Net Worth tab: when ?cat= matches a category's bare
  // name, expand it, scroll it into view, then drop the param so a manual
  // collapse later actually sticks.
  useEffect(() => {
    if (!catParam || visibleCategories.length === 0) return;
    const want = catParam.toLowerCase();
    const target = visibleCategories.find(
      (c) => splitCategoryEmoji(c.name).name.toLowerCase() === want,
    );
    if (!target) return;
    setExpandedSet((prev) => {
      if (prev.has(target.name)) return prev;
      const next = new Set(prev);
      next.add(target.name);
      return next;
    });
    requestAnimationFrame(() => {
      document
        .getElementById(categoryDomId(target.name))
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('cat');
        return next;
      },
      { replace: true },
    );
  }, [catParam, visibleCategories, setSearchParams]);

  const body = (
    <>
      {error && <div className="alert-error">{error}</div>}

      {!data && isLoading && (
        <div className="card text-center py-12 text-fg-secondary">Loading…</div>
      )}

      {debts && (
        <>
          <div className="grid grid-cols-3 gap-5 max-md:grid-cols-1">
            <div className="col-span-2 max-md:col-span-1">
              <HeroDebts debts={debts} scope={scope} />
            </div>
            <DebtsBreakdownCard debts={debts} scope={scope} />
          </div>

          {visibleCategories.length === 0 || totalBalance === 0 ? (
            <div className="card empty-state">
              No debt recorded for{' '}
              {scope === 'household' ? 'this household' : getPersonName(scope)}
              .
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {visibleCategories
                .filter((c) => pickScope(c.balance, scope) !== 0)
                .map((c) => (
                  <DebtCategoryCard
                    key={c.name}
                    category={c}
                    scope={scope}
                    totalBalance={totalBalance}
                    expanded={expandedSet.has(c.name)}
                    onToggle={() => toggle(c.name)}
                    domId={categoryDomId(c.name)}
                  />
                ))}
            </div>
          )}
        </>
      )}
    </>
  );

  // Embedded inside NetWorthPage's tabs: caller already provides the page
  // wrapper and header, so render the body bare and rely on the parent's
  // `space-y-5` for vertical rhythm between the embedded sections.
  if (embedded) {
    return <div className="space-y-5">{body}</div>;
  }

  return (
    <div className="page-container space-y-5">
      <header className="page-header">
        <div>
          <h1 className="page-title">Debts</h1>
          <p className="page-subtitle">What you owe and what it's costing each month</p>
        </div>
      </header>
      {body}
    </div>
  );
}
